import { User, Users, UserCog, Building2, Check } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { OwnershipFilterValue } from '@/api/visits';

// 'all' означает показать всех клиентов (для админов)
type FilterValue = OwnershipFilterValue[] | 'all';

interface OwnershipFilterProps {
  value: FilterValue;
  onChange: (value: FilterValue) => void;
}

const FILTER_OPTIONS: { value: OwnershipFilterValue; label: string; icon: LucideIcon }[] = [
  { value: 'own', label: 'Мои', icon: User },
  { value: 'additionalOffice', label: 'Доп.офисы', icon: Building2 },
  { value: 'subordinates', label: 'Клиенты сотрудников', icon: UserCog },
  { value: 'delegated', label: 'Делегированные', icon: Users },
];

export function OwnershipFilter({ value, onChange }: OwnershipFilterProps) {
  const isAll = value === 'all';
  const selectedFilters = isAll ? [] : value;

  const toggleFilter = (filter: OwnershipFilterValue) => {
    if (isAll) {
      // Переключаемся с "все" на конкретный фильтр
      onChange([filter]);
      return;
    }

    if (selectedFilters.includes(filter)) {
      // Не даём снять последний фильтр
      if (selectedFilters.length > 1) {
        onChange(selectedFilters.filter((v) => v !== filter));
      }
    } else {
      onChange([...selectedFilters, filter]);
    }
  };

  const selectAllClients = () => {
    onChange('all');
  };

  // Получаем иконки для отображения в кнопке (в фиксированном порядке)
  const buttonIcons = isAll
    ? []
    : FILTER_OPTIONS.filter((o) => selectedFilters.includes(o.value)).map((o) => o.icon);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 px-2">
          {isAll ? (
            <span className="text-xs">Все</span>
          ) : (
            <div className="flex items-center gap-0.5">
              {buttonIcons.map((Icon, i) => (
                <Icon key={i} className="h-4 w-4" />
              ))}
            </div>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        {/* Все клиенты (для админов) */}
        <div
          className="flex items-center gap-2 px-2 py-2 cursor-pointer hover:bg-accent rounded-sm"
          onClick={selectAllClients}
        >
          <div
            className={`w-4 h-4 border rounded flex items-center justify-center ${
              isAll ? 'bg-primary border-primary' : 'border-input'
            }`}
          >
            {isAll && <Check className="h-3 w-3 text-primary-foreground" />}
          </div>
          <span className="text-sm">Все клиенты</span>
        </div>

        <div className="border-t my-1" />

        {/* Отдельные фильтры */}
        {FILTER_OPTIONS.map((opt) => {
          const isSelected = !isAll && selectedFilters.includes(opt.value);
          const Icon = opt.icon;
          return (
            <div
              key={opt.value}
              className="flex items-center gap-2 px-2 py-2 cursor-pointer hover:bg-accent rounded-sm"
              onClick={() => toggleFilter(opt.value)}
            >
              <div
                className={`w-4 h-4 border rounded flex items-center justify-center ${
                  isSelected ? 'bg-primary border-primary' : 'border-input'
                }`}
              >
                {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
              </div>
              <Icon className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{opt.label}</span>
            </div>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
